import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { 
  Target, 
  Eye, 
  Heart, 
  Award, 
  Shield,
  CheckCircle,
  ArrowRight,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const values = [
  { icon: Shield, title: 'Safety', description: 'Keselamatan kerja adalah prioritas utama' },
  { icon: Award, title: 'Quality', description: 'Standar kualitas tertinggi dalam setiap produk' },
  { icon: Target, title: 'Technology', description: 'Teknologi modern untuk hasil optimal' },
  { icon: Heart, title: 'Innovation', description: 'Inovasi berkelanjutan untuk solusi terbaik' },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Tentang Kami</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Profil <span className="text-[#FFC107]">Perusahaan</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Mengenal lebih dekat PT IMA MONTAZ TEKNINDO sebagai mitra terpercaya 
              dalam solusi engineering dan manufacturing industri.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Profile Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
                PT. IMA MONTAZ TEKNINDO
              </h2>
              <div className="text-slate-600 leading-relaxed space-y-4">
                <p className="font-medium text-[#001582]">
                  PT. IMA MONTAZ TEKNINDO adalah perusahaan yang terlahir dari kondisi semangat individu yang ingin terus mengabdikan karyanya kepada masyarakat luas dengan izin dan ridho Allah SWT.
                </p>
                <p>
                  PT. IMA MONTAZ TEKNINDO adalah rentetan panjang dari cacatan berkarir pendiri perusahaan, dengan berbagai macam pengalaman mendasar yang telah dan sedang dialami serta dilaluinya, sehingga dari terpaan proses alami tersebut menjadikan pembelajaran dasar dan sekaligus sebagai terapi mental di dalam mencapai tingkat pendewasaan dan kematangan di dalam berbisnis.
                </p>
                <p>
                  Kami sadari situasi Global Market dalam persaingan yang ketat pada dunia bisnis era saat ini, dimana suatu perusahaan sangat diperlukan adanya kesiapan secara menyeluruh dari segala aspek-aspek pendukung yang menjadi satu kesatuan yang utuh, besar dan kokoh sehingga akan mampu untuk dapat bekerja secara sistematis dan profesional, dengan demikian diharapkan perusahaan akan siap dan mampu dalam memasuki persaingan bisnis global secara sehat dan berkesinambungan.
                </p>
                <p>
                  Pengalaman kerja secara professional dan hubungan yang cukup baik dari masing-masing individu yang pernah terjalin merupakan pendukung modal dasar yang cukup penting dalam menunjang kelangsungan perusahaan ini.
                </p>
                <p>
                  Data pendukung dan pengalaman yang kami sajikan disini dapat memberikan gambaran mengenai ruang lingkup dari bidang usaha yang kami kembangkan meliputi Supply, Manufacture, Repair of Mechanical Electrical Equipment Parts and Fabricated Metal Product for Oil Gas and Industrial.
                </p>
                <p>
                  Demikian pengantar ini kami sampaikan, semoga perusahaan kami dapat diberikan kepercayaan untuk melaksanakan pekerjaan yang ada baik dari instansi pemerintah maupun swasta.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/2br3moia_dji.jpg"
                alt="Workshop PT IMA MONTAZ TEKNINDO"
                className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-2xl p-6 shadow-xl">
                <p className="font-heading font-bold text-3xl text-[#FFC107]">ISO</p>
                <p className="text-white text-sm">9001:2015 Certified</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-32 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-slate-100"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#FFC107]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-4">Visi</h3>
              <div className="text-slate-600 leading-relaxed space-y-4">
                <p>
                  Dengan prinsip kemitraan dan kepercayaan, pengembangan perusahaan yang signifikan dan kontinyuitas di masa mendatang akan menjadi dasar optimisme kami dalam mengikuti kemajuan jaman yang semakin kompleks.
                </p>
                <p>
                  Membangun secara profesional dan bertanggung jawab atas mutu pekerjaan yang dikerjakan menjalin Mitra untuk berkarya menghasilkan hal yang nyata.
                </p>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-slate-100"
            >
              <div className="w-16 h-16 bg-[#FFC107] rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#001582]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-4">Misi</h3>
              <div className="text-slate-600 leading-relaxed space-y-4">
                <p>
                  Memperluas kesempatan bersama Sumber Daya Manusia yang ada dan mampu secara profesional dalam mengembangkan karier dan tujuan akhir yang sempurna.
                </p>
                <p>
                  Memberikan kepuasan kepada setiap mitra dalam bentuk hasil kerja dan mutu yang dapat dibanggakan.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Nilai-Nilai <span className="text-[#001582]">Perusahaan</span>
            </h2>
            <p className="text-slate-600 text-lg">
              Fondasi yang mendasari setiap aktivitas dan keputusan kami.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#f5f7fb] rounded-2xl p-6 text-center hover:bg-gradient-to-r hover:from-[#001582] hover:to-[#001ea7] hover:text-white group transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-r from-[#001582] to-[#001ea7] group-hover:bg-[#FFC107] rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors">
                  <value.icon className="w-7 h-7 text-[#FFC107] group-hover:text-[#001582] transition-colors" />
                </div>
                <h4 className="font-heading font-bold text-lg mb-2 group-hover:text-white transition-colors">
                  {value.title}
                </h4>
                <p className="text-slate-600 text-sm group-hover:text-slate-300 transition-colors">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#001582] to-[#001ea7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
            Tertarik untuk bekerja sama?
          </h3>
          <a 
            href="https://wa.me/6285210005588" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="bg-[#FFC107] text-[#001582] hover:bg-[#FFD54F] font-bold px-8 py-6 rounded-xl"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Hubungi Kami
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
